<template>
    <div class="well"
         :class="{'-dropping': (dropping && state === 'waiting')}"
         @drop.prevent="handleDrop"
         @dragenter.prevent="ddEnter"
         @dragover.prevent="ddEnter"
         @dragleave.prevent="ddLeave">
        <div v-if="state === 'waiting'">
            <div class="drop-zone f -a-center -gutter">
                <a class="btn btn-default"
                   v-on:click.prevent="chooseFile">
                    Choisir un fichier
                </a>
                <div class="separator f -a-center">
                    <div class="text">ou</div>
                </div>
                <div class="explain expand">
                    Glisser/déposer un fichier
                </div>
            </div>
        </div>
        <div v-if="state === 'uploading'"
             class="f -a-center -gutter">
            <div class="file-name">
                <i class="fa icon" :class="'fa-' + mimeIcon"></i>
                {{ file.name }}
            </div>
            <div class="progress progress-striped active expand">
                <div class="progress-bar"
                     v-bind:style="{width: (progress * 100) + '%'}"></div>
            </div>
        </div>
        <div class="f -a-center -gutter display-file"
             v-if="state === 'displaying'">
            <a :href="info.content"
               target="_blank"
               class="btn btn-link expand -filename">
                <i class="fa icon" :class="'fa-' + mimeIcon"></i>
                {{ info.content | filename }}
            </a>
            <div class="confirm-container">
                <a @click.prevent="confirm"
                   href="#">
                    <i class="glyphicon glyphicon-remove"></i>
                </a>
                <div class="popover fade-transition left -confirm"
                     :class="{'-visible': showConfirm}">
                    <div class="arrow"></div>
                    <h3 class="popover-title">Confirmer&nbsp;?</h3>
                    <div class="popover-content f -a-center -gutter">
                        <a @click.prevent="cancelDrop"
                           class="btn btn-link">
                            Annuler
                        </a>
                        <a @click.prevent="drop"
                           class="btn btn-primary">
                            Supprimer
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped
       lang="less">
    @import '~src/style/variables';
    @import '~bootstrap/less/mixins.less';

    @sep-color: lighten(@text-color, 30%);
    @sep-height: 46px;

    .display-file .btn {
        text-align: left;
    }

    .well {
        border: 2px dashed transparent;

        &.-dropping {
            border-color: @text-color;
        }
    }

    .drop-zone {
        position: relative;

        .explain, .separator {
            text-align: center;
        }

        .separator {
            position: relative;
            height: @sep-height;

            &::before {
                content: " ";
                position: absolute;
                height: 100%;
                width: 1px;
                background-color: @sep-color;
                top: 0;
                left: 50%;
            }

            .text {
                z-index: 1;
                color: @sep-color;
                background-color: @well-bg;
            }
        }
    }

    .progress {
        margin-bottom: 0;
    }

    .file-name {
        display: inline-block;
        font-weight: @btn-font-weight;
        text-align: center;
        vertical-align: middle;
        white-space: nowrap;
        .button-size(
            @padding-base-vertical;
            @padding-base-horizontal;
            @font-size-base;
            @line-height-base;
            @btn-border-radius-base
        );
        border: 1px solid @btn-primary-bg;
        max-width: (100% / 3);
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .icon {
        transform-origin: 0 50%;
        transform: scale(2);
        margin-right: (@grid-gutter-width / 2);
    }

    .confirm-container {
        position: relative;
    }

    .popover.-confirm {
        display: block;
        position: absolute;
        right: 30px;
        left: auto;
        top: 50%;
        transform: translateY(-50%);
        transition: ease 300ms;
        visibility: hidden;
        opacity: 0;

        &.-visible {
            visibility: visible;
            opacity: 1;
        }
    }

    .btn.-filename {
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>

<!--suppress ExceptionCaughtLocallyJS -->
<script type="text/ecmascript-6">
    import {upload} from '../../api/files';
    import {manager as msgManager} from '../../messages';
    import {smartSize, items} from '../../utils';
    import config from '../../config';

    export default {
        data() {
            return {
                file: undefined,
                showConfirm: false,
                dropping: false,
                input: undefined,
                progress: 0
            };
        },
        props: {
            allowedMimes: {
                'default': () => config.ALLOWED_DOCUMENT_TYPES
            },
            info: {
                twoWay: true
            },
            max: {
                'default': config.UPLOAD_MAX_SIZE
            }
        },
        computed: {
            state() {
                if (this.file) {
                    return 'uploading';
                }

                if (this.info) {
                    return 'displaying';
                }

                return 'waiting';
            },
            maxDisp() {
                return smartSize(this.max);
            },
            mime() {
                if (this.info) {
                    return this.info.mime;
                }

                if (this.file) {
                    return this.file.type;
                }
            },
            mimeIcon() {
                return this.$options.filters.mimeIcon(this.mime);
            }
        },
        methods: {
            chooseFile() {
                this.input.click();
            },
            hasFile: async function (file) {
                if (this.state !== 'waiting') {
                    return;
                }

                this.resetInput();

                if (!this.allowedMimes.hasOwnProperty(file.type)) {
                    const allowed = [];

                    for (const [, v] of items(this.allowedMimes)) {
                        allowed.push(v);
                    }

                    return msgManager.push(
                        'Attention.',
                        `Type de fichier non autorisé. Attendu: ${allowed.join(', ')}`,
                        'warning'
                    );
                }

                if (file.size > config.UPLOAD_MAX_SIZE) {
                    return msgManager.push(
                        'Attention.',
                        `Ce fichier est trop gros. Merci de vous limiter à ${this.maxDisp}`,
                        'warning'
                    );
                }

                this.file = file;

                try {
                    const info = await upload(file, (p) => {
                        this.progress = p;
                    });

                    if (!info.json) {
                        throw new Error();
                    }

                    this.info = info.json;
                } catch (e) {
                    msgManager.push(
                        'Erreur.',
                        'Impossible d\'envoyer le fichier. Merci d\'essayer à nouveau plus tard.',
                        'danger'
                    );
                } finally {
                    this.file = undefined;
                    this.progress = 0;
                    this.showConfirm = false;
                }
            },
            confirm() {
                this.showConfirm = true;
            },
            cancelDrop() {
                this.showConfirm = false;
            },
            drop() {
                this.info = undefined;
                this.showConfirm = false;
            },
            ddEnter(e) {
                if (e.dataTransfer.types.length !== 1 || e.dataTransfer.types[0] !== 'Files') {
                    this.dropping = false;
                }

                this.dropping = true;
            },
            ddLeave() {
                this.dropping = false;
            },
            handleDrop(e) {
                this.dropping = false;

                if (e.dataTransfer.types.length !== 1 || e.dataTransfer.types[0] !== 'Files') {
                    return;
                }

                this.hasFile(e.dataTransfer.files[0]);
            },
            handleFileChange() {
                this.hasFile(this.input.files[0]);
            },
            resetInput() {
                if (this.input) {
                    this.input.removeEventListener('change', this.handleFileChange);
                    this.$el.removeChild(this.input);
                    this.input = undefined;
                }

                this.input = document.createElement('INPUT');
                this.input.setAttribute('type', 'file');
                this.input.style.display = 'none';
                this.input.addEventListener('change', this.handleFileChange);

                this.$el.appendChild(this.input);
            }
        },
        watch: {
            state() {
                if (this.state === 'waiting') {
                    this.resetInput();
                }
            }
        },
        ready() {
            this.resetInput();
        }
    };
</script>



// WEBPACK FOOTER //
// EditableFile.vue?3a90ab6b